import moment from "moment";
import React, { useEffect, useState } from "react";
import {
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline";
import {
  UserPlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowDownIcon,
} from "@heroicons/react/24/solid";
import {
  Dialog,
  Card,
  CardHeader,
  Input,
  Chip,
  Typography,
  Button,
  CardBody,
  CardFooter,
  IconButton,
  Tooltip,
  Select,
  Option,
  Textarea,
} from "@material-tailwind/react";
import {
  getPolicyManagementData,
  createPolicyData,
  editPolicyData,
  deletePolicy,
  getUserData,
} from "../../helperFunctions/apiFunction";
import { useAuthUserToken } from "../../hooks/useAuthUserToken";
import TableSkeleton from "../../components/Loaders/TableSkeleton";
import toast from "react-hot-toast";
import LoadingButton from "../../components/Loaders/LoadingButton";
import ExcelJS from "exceljs";
import PdfView from "../../components/views/PdfView";
import { useNavigate } from "react-router-dom";

const TABLE_HEAD = [
  "Title",
  "Owner",
  "Frequency",
  "Compliance",
  "Status",
  "Review Status",
  "Due Date",
  "Action",
];

export default function PolicyManagement() {
  const { refreshToken, rbac } = useAuthUserToken();
  const [templateData, setTemplateData] = useState([]);
  const [userData, setUserData] = useState([]);
  const [isLoading, setIsLoading] = useState(null);
  const [searchBy, setSearchBy] = useState("");
  const [sortBy, setSortBy] = useState("asc");
  const [pdfData, setPdfData] = useState("");
  const [pdfView, setPdfView] = useState(false);
  const [status, setStatus] = useState("");
  // const [compliantStatus, setCompliantStatus] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [errors, setErrors] = useState({ name: "" });
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [id, setId] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [reloadData, setReloadData] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [docTitle, setDocTitle] = useState("");
  const navigate = useNavigate();

  const handleSelect = (row, _id, owner, docTitle) => {
    setPdfView(true);
    setId(_id);
    setDocTitle(docTitle);
    setOwnerId(owner?.id ?? "");
    setPdfData(row);
  };

  const handleOpenCreateDialog = () => {
    setOpenCreateDialog((cur) => !cur);
  };

  const [editFormData, setEditFormData] = useState({
    docTitle: "",
    docType: "",
    docVersion: "",
    owner: "",
    frequency: "",
    status: "",
    file: null,
    description: "",
  });

  const [open, setOpen] = useState(false);
  const handleOpen = (item) => {
    setEditFormData({
      docTitle: item?.docTitle || "",
      docType: item?.docType || "",
      docVersion: item?.docVersion || "",
      owner: item?.owner?.id || "",
      frequency: item?.frequency || "",
      status: item?.status || "",
      description: item?.description || "",
    });
    setId(item?._id || "");
    setErrors({ name: "" });
    setOpen((cur) => !cur);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    setEditFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChangeEditSelect = (e, fieldName) => {
    setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
    setEditFormData((prevData) => ({
      ...prevData,
      [fieldName]: e,
    }));
  };

  const handleEditFileUpload = (event) => {
    const newErrors = { ...errors };
    const selectedFile = event.target.files[0];

    if (selectedFile) {
      setEditFormData({
        ...editFormData,
        file: selectedFile,
      });
      newErrors.file = "";
    } else {
      newErrors.file = "File is required";
    }

    setErrors(newErrors);
  };

  const handleEditBlur = () => {
    validateEditForm();
  };

  const validateEditForm = () => {
    const newErrors = {};
    if (!editFormData.docTitle.trim()) newErrors.docTitle = "Title is required";
    // if (!editFormData.docType) newErrors.docType = "Type is required";
    if (!editFormData.docVersion.trim())
      newErrors.docVersion = "Version is required";
    if (!editFormData?.owner?.trim()) newErrors.owner = "Owner is required";
    if (!editFormData.frequency.trim())
      newErrors.frequency = "Revision Frequency is required";
    if (!editFormData.description)
      newErrors.description = "Description is required";
    setErrors(newErrors);
  };

  useEffect(() => {
    getPolicyManagementData(
      refreshToken,
      searchBy,
      sortBy,
      limit,
      page,
      status,
      // compliantStatus
    ).then((resp) => {
      if (resp?.code === 403) {
        navigate('/403');
        toast.error(resp?.message, {
          className: "text-[12px]",
        });
      }
      if (resp?.code === 440)
        toast.error(resp?.message, {
          className: "text-[12px]",
        });
      setTotalPage(resp?.data?.totalPages);
      setTemplateData(resp?.data?.results);
      setIsLoading(false);
    });

    getUserData(refreshToken, searchBy, sortBy, 100, page, status).then(
      (resp) => {
        if (resp?.code === 440)
          toast.error(resp?.message, {
            className: "text-[12px]",
          });
        setUserData(resp?.data?.results);
      }
    );
  }, [page, limit, sortBy, searchBy, status, reloadData]);

  const handleDelete = (id) => {
    setIsLoadingButton(true);
    deletePolicy(refreshToken, id)
      .then((response) => {
        if (response?.code === 200 || response?.code === 201) {
          toast.success("Policy Data deleted successfully", {
            className: "text-[12px]",
          });
        } else if (response.code === 400) {
          toast.error(response?.message, {
            className: "text-[12px]",
          });
        } else {
          toast.error(response?.message, {
            className: "text-[12px]",
          });
        }
      })
      .catch((error) => {
        toast.error("Something went wrong!", {
          className: "text-[12px]",
        });
        console.error("Error during login:", error);
      })
      .finally(() => {
        setIsLoadingButton(false);
        setId("");
        setDeleteDialog(false);
        setReloadData((cur) => !cur);
      });
  };

  const handleDeleteDialog = (id) => {
    setId(id);
    setDeleteDialog((cur) => !cur);
  };
  // Create Form Functionality

  const formSetData = {
    docTitle: "",
    docType: "",
    docVersion: "",
    owner: "",
    frequency: "",
    status: "",
    file: null,
    description: "",
  };

  const [formData, setFormData] = useState({
    docTitle: "",
    docType: "",
    docVersion: "",
    owner: "",
    frequency: "",
    status: "",
    file: null,
    description: "",
  });

  const validateForm = () => {
    const newErrors = {};
    if (!formData.docTitle.trim()) newErrors.docTitle = "Title is required";
    if (!formData?.docType?.trim()) newErrors.docType = "Type is required";
    if (!formData.docVersion.trim())
      newErrors.docVersion = "Version is required";
    if (!formData.owner.trim()) newErrors.owner = "Owner is required";
    if (!formData.frequency.trim())
      newErrors.frequency = "Revision Frequency is required";
    if (!formData.status.trim()) newErrors.status = "Status is required";
    if (!formData.file) newErrors.file = "File is required";
    if (!formData.description)
      newErrors.description = "Description is required";
    setErrors(newErrors);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChangeSelect = (e, fieldName) => {
    setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: e,
    }));
  };

  const handleBlur = () => {
    validateForm();
  };

  const handleFileUpload = (event) => {
    const newErrors = { ...errors };
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFormData({
        ...formData,
        file: selectedFile,
      });
      newErrors.file = "";
    } else {
      newErrors.file = "File is required";
    }

    setErrors(newErrors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    validateForm();
    if (Object.keys(errors).length === 0) {
      setIsLoadingButton(true);
      let formDataFinal = new FormData();
      formDataFinal.append("docTitle", formData.docTitle);
      formDataFinal.append("docType", formData.docType);
      formDataFinal.append("docVersion", formData.docVersion);
      formDataFinal.append("owner", formData.owner);
      formDataFinal.append("frequency", formData.frequency);
      formDataFinal.append("status", formData.status);
      formDataFinal.append("file", formData.file);
      formDataFinal.append("description", formData.description);
      createPolicyData(refreshToken, formDataFinal)
        .then((response) => {
          if (response?.code === 200 || response?.code === 201) {
            toast.success("Policy Data created successfully", {
              className: "text-[12px]",
            });
          } else if (response?.code === 400 || response?.code === 404) {
            toast.error(response?.error, {
              className: "text-[12px]",
            });
          } else {
            toast.error("Something went wrong!", {
              className: "text-[12px]",
            });
          }
        })
        .catch((error) => {
          toast.error(error?.message, {
            className: "text-[12px]",
          });
          console.error("Error during login:", error);
        })
        .finally(() => {
          setIsLoadingButton(false);
          setFormData(formSetData);
          setReloadData((cur) => !cur);
          setOpenCreateDialog((cur) => !cur);
        });
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    validateEditForm();
    if (Object.keys(errors).length === 0) {
      setIsLoadingButton(true);
      editPolicyData(refreshToken, editFormData, id)
        .then((response) => {
          if (response?.code === 200 || response?.code === 201) {
            toast.success(response?.message, {
              className: "text-[12px]",
            });
          } else if (response?.code === 400 || response?.code === 404) {
            toast.error(response?.message, {
              className: "text-[12px]",
            });
          } else {
            toast.error(response?.message, {
              className: "text-[12px]",
            });
          }
        })
        .catch((error) => {
          toast.error("Something went wrong!", {
            className: "text-[12px]",
          });
        })
        .finally(() => {
          setFormData(formSetData);
          setIsLoadingButton(false);
          setOpen((cur) => !cur);
          setReloadData((cur) => !cur);
        });
    }
  };
  // End Create Form Functionality

  const exportToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("PolicyManagement Data");
    const headers = ["S.No.", "Policy Name", "Owner", "Status"];
    worksheet.addRow(headers);
    templateData.forEach((row, index) => {
      worksheet.addRow([index + 1, row?.docTitle, row?.owner, row?.status]);
    });
    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "policyManagement_data.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <div className="app-main__inner !pl-20 ">
      <Card
        className="h-full w-full rounded-3xl shadow-xl"
        style={{ backgroundImage: `url('/assets/images/beams.jpg')` }}
      >
        <CardHeader
          floated={false}
          shadow={false}
          className=" bg-transparent rounded-none !overflow-visible"
        >
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between md:gap-8">
            <div>
              <Typography variant="h5" color="blue-gray">
                List of Policy Documents
              </Typography>
              <Typography color="gray" className="mt-1 font-normal">
                View all organization information security policy documents
              </Typography>
            </div>
            <div className="flex flex-row md:flex-row items-center justify-between md:items-center gap-2 md:gap-8 mt-4 md:mt-0">
              {!pdfView ? (
                <Button
                  variant="outlined"
                  size="sm"
                  onClick={exportToExcel}
                  loading={isLoading === null ? true : false}
                >
                  <ArrowDownIcon strokeWidth={2} className="h-4 w-4" />
                </Button>
              ) : null}
              {!pdfView && rbac?.policyAdd ? (
                <>
                  <Button
                    onClick={handleOpenCreateDialog}
                    className="flex items-center gap-3"
                    size="sm"
                    loading={isLoading === null ? true : false}
                  >
                    <UserPlusIcon strokeWidth={2} className="h-4 w-4" />
                    <span className="hidden md:inline-block"> Create New</span>
                  </Button>
                </>
              ) : null}
            </div>
          </div>
          {!pdfView && (
            <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
                <Select
                  color="blue"
                  label="Select Limit"
                  className="!border-t-0 !border-[#b0bec5]"
                  onChange={(event) => {
                    setLimit(event);
                    setIsLoading(null);
                  }}
                  value={limit}
                >
                  <Option value={10}>10</Option>
                  <Option value={20}>20</Option>
                  <Option value={30}>30</Option>
                  <Option value={40}>40</Option>
                  <Option value={50}>50</Option>
                </Select>
                <Select
                  color="purple"
                  label="Sort By"
                  className="!border-t-0 !border-[#b0bec5]"
                  onChange={(event) => {
                    setSortBy(event);
                    setIsLoading(null);
                  }}
                  value={sortBy}
                >
                  <Option value="asc">Ascending</Option>
                  <Option value="desc">Descending</Option>
                </Select>
                {/* <Select
                  color="green"
                  label="Compliant Status"
                  className="!border-t-0 !border-[#b0bec5]"
                  onChange={(event) => {
                    setCompliantStatus(event);
                    setIsLoading(null);
                  }}
                  value={compliantStatus}
                >
                  <Option value="">All Data</Option>
                  <Option value="compliant">Compliant</Option>
                  <Option value="nonCompliant">Non Compliant</Option>
                </Select> */}
                <Select
                  color="yellow"
                  label="Review Status"
                  className="!border-t-0 !border-[#b0bec5]"
                  onChange={(event) => {
                    setStatus(event);
                    setIsLoading(null);
                  }}
                  value={status}
                >
                  <Option value="">All Data</Option>
                  <Option value="pending">Pending</Option>
                  <Option value="reviewed">Reviewed</Option>
                </Select>
              </div>
              <div className="w-full md:w-auto">
                <Input
                  label="Search"
                  value={searchBy}
                  className="focus:!border-black focus:!border-2 focus:!border-t-0"
                  onChange={(e) => setSearchBy(e.target.value)}
                  icon={<MagnifyingGlassIcon className="h-5 w-5" />}
                />
              </div>
            </div>
          )}
        </CardHeader>

        {pdfView ? (
          <>
            <PdfView
              setPdfView={setPdfView}
              pdfData={pdfData}
              owner={ownerId}
              policyId={id}
              docTitle={docTitle}
            />
          </>
        ) : (
          <CardBody className="overflow-scroll px-3 p-0">
            <table className="mt-4 w-full min-w-max table-auto text-left">
              <thead>
                <tr>
                  {TABLE_HEAD.map((head, index) => (
                    <th
                      key={head}
                      className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-3 transition-colors hover:bg-blue-gray-50"
                    >
                      <Typography
                        variant="small"
                        color="blue-gray"
                        className="flex items-center justify-between gap-2 font-bold leading-none opacity-70"
                      >
                        {head}{" "}
                        {index !== TABLE_HEAD.length - 1 && (
                          <ChevronUpDownIcon
                            strokeWidth={2}
                            className="h-4 w-4"
                          />
                        )}
                      </Typography>
                    </th>
                  ))}
                </tr>
              </thead>
              {isLoading === null ? (
                <>
                  <tbody>
                    <tr>
                      <td colSpan={TABLE_HEAD.length}>
                        <TableSkeleton />
                      </td>
                    </tr>
                  </tbody>
                </>
              ) : (
                <>
                  <tbody>
                    {isLoading === false && templateData?.length > 0 ? (
                      templateData.map((item, index) => {
                        if (!item) return null;
                        const {
                          docTitle,
                          docType,
                          docVersion,
                          owner,
                          frequency,
                          verificationDates,
                          status,
                          file,
                          description,
                          _id,
                        } = item;
                        const currentDate = new Date();
                        const currentMonth = currentDate.getMonth();
                        const currentYear = currentDate.getFullYear();
                        let startMonth = 0;
                        let reviewStatus = false;
                        let compliantStatus = false;
                        let dueDate;
                        if (frequency === "monthly") {
                          dueDate = moment().endOf("month").format("ll");

                          const currentMonthEntry = verificationDates.findIndex(
                            (entry) =>
                              entry.month === currentMonth &&
                              entry.year === currentYear
                          );
                          reviewStatus =
                            verificationDates[currentMonthEntry].isVerified;
                          if (currentMonthEntry === 0) compliantStatus = true;
                          else if (currentMonthEntry > 0)
                            compliantStatus =
                              verificationDates[currentMonthEntry - 1]
                                .isVerified;
                        } else if (frequency === "quarterly") {
                          if (currentMonth >= 0 && currentMonth <= 2) {
                            startMonth = 0;
                          } else if (currentMonth >= 3 && currentMonth <= 5) {
                            startMonth = 3;
                          } else if (currentMonth >= 6 && currentMonth <= 8) {
                            startMonth = 6;
                          } else if (currentMonth >= 9 && currentMonth <= 11) {
                            startMonth = 9;
                          }
                          const startDate = moment()
                            .year(currentYear)
                            .month(startMonth)
                            .startOf("month");

                          dueDate = startDate
                            .add(2, "months")
                            .endOf("month")
                            .format("ll");

                          const currentMonthEntry = verificationDates.findIndex(
                            (entry) =>
                              entry.month === startMonth &&
                              entry.year === currentYear
                          );
                          reviewStatus =
                            verificationDates[currentMonthEntry].isVerified;
                          if (currentMonthEntry === 0) compliantStatus = true;
                          else if (currentMonthEntry > 0)
                            compliantStatus =
                              verificationDates[currentMonthEntry - 1]
                                .isVerified;
                        } else if (frequency === "half-yearly") {
                          if (currentMonth >= 0 && currentMonth < 6) {
                            startMonth = 0;
                          } else {
                            startMonth = 6;
                          }
                          const startDate = moment()
                            .year(currentYear)
                            .month(startMonth)
                            .startOf("month");

                          dueDate = startDate
                            .add(5, "months")
                            .endOf("month")
                            .format("ll");
                          const currentMonthEntry = verificationDates.findIndex(
                            (entry) =>
                              entry.month === startMonth &&
                              entry.year === currentYear
                          );
                          reviewStatus =
                            verificationDates[currentMonthEntry].isVerified;
                          if (currentMonthEntry === 0) compliantStatus = true;
                          else if (currentMonthEntry > 0)
                            compliantStatus =
                              verificationDates[currentMonthEntry - 1]
                                .isVerified;
                        } else if (frequency === "yearly") {
                          const currentMonthEntry = verificationDates.findIndex(
                            (entry) =>
                              entry.month === startMonth &&
                              entry.year === currentYear
                          );
                          const startDate = moment()
                            .year(currentYear)
                            .month(startMonth)
                            .startOf("month");

                          dueDate = startDate
                            .add(11, "months")
                            .endOf("month")
                            .format("ll");
                          reviewStatus =
                            verificationDates[currentMonthEntry].isVerified;
                          if (currentMonthEntry === 0) compliantStatus = true;
                          else if (currentMonthEntry > 0)
                            compliantStatus =
                              verificationDates[currentMonthEntry - 1]
                                .isVerified;
                        }
                        const isLast = index === templateData.length - 1;
                        const classes = isLast
                          ? "p-3"
                          : "p-3 border-b border-blue-gray-50";

                        return (
                          <tr key={`${_id}-${index}`}>
                            <td className={classes}>
                              <div className="flex items-center gap-3">
                                <button
                                  variant="small"
                                  color="blue-gray"
                                  className="font-normal underline-offset-2 underline"
                                  onClick={() => {
                                    handleSelect(item, _id, owner, docTitle);
                                  }}
                                >
                                  {docTitle}
                                </button>
                              </div>
                            </td>
                            <td className={classes}>
                              {owner ? (
                                <Typography
                                  variant="small"
                                  color="blue-gray"
                                  className="font-normal"
                                >
                                  {owner.fullName}
                                </Typography>
                              ) : (
                                <Chip
                                  variant="ghost"
                                  size="sm"
                                  value="Unknown User"
                                  className="rounded-full w-fit text-xs	font-thin	"
                                />
                              )}
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal capitalize"
                              >
                                {frequency}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <div className="w-max">
                                <Chip
                                  variant="ghost"
                                  size="sm"
                                  value={
                                    compliantStatus
                                      ? "Compliant"
                                      : "Non-Compliant"
                                  }
                                  color={compliantStatus ? "green" : "red"}
                                />
                              </div>
                            </td>
                            <td className={classes}>
                              <div className="w-max">
                                <Chip
                                  variant="ghost"
                                  size="sm"
                                  value={status}
                                  color={status === "active" ? "green" : "red"}
                                />
                              </div>
                            </td>
                            <td className={classes}>
                              <div className="w-max">
                                <Chip
                                  variant="ghost"
                                  size="sm"
                                  value={reviewStatus ? "Reviewed" : "Pending"}
                                  color={reviewStatus ? "green" : "red"}
                                />
                              </div>
                            </td>
                            <td className={classes}>
                              <Chip
                                variant="ghost"
                                size="sm"
                                value={dueDate}
                                className="rounded-full w-fit text-xs	normal-case font-medium	"
                              />
                            </td>
                            <td className={classes}>
                              <div className="flex gap-1.5">
                                {rbac?.ploicyEdit || rbac?.policyAdd ? (
                                  <>
                                    {rbac?.policyEdit && (
                                      <Tooltip
                                        className="bg-blue-gray-500"
                                        content="Edit"
                                      >
                                        <IconButton
                                          variant="text"
                                          onClick={() =>
                                            handleOpen({
                                              docTitle,
                                              docType,
                                              docVersion,
                                              owner,
                                              frequency,
                                              status,
                                              file,
                                              description,
                                              _id,
                                            })
                                          }
                                        >
                                          <PencilIcon className="h-4 w-4" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {rbac?.policyDel && (
                                      <Tooltip
                                        className="bg-blue-gray-500"
                                        content="Delete Record"
                                      >
                                        <IconButton
                                          variant="text"
                                          onClick={() => handleDeleteDialog(_id)}
                                        >
                                          <TrashIcon className="h-4 w-4" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </>
                                ) : (
                                  <td></td>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="8">
                          <div className="flex flex-col items-center w-full">
                            <img
                              height={250}
                              width={250}
                              quality={100}
                              src="/assets/images/no-data-found.png"
                              className="w-1/4 mix-blend-darken"
                              alt=""
                            />
                            <p className="text-[18px] font-extralight">
                              No Data Found!
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </>
              )}
            </table>
          </CardBody>
        )}
        {isLoading === null ? null : (
          <>
            <CardFooter className="flex items-center justify-between border-t border-blue-gray-50 p-4">
              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setIsLoading(null);
                  setPage(page - 1);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Previous
              </Button>
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPage }, (_, index) => (
                  <IconButton
                    key={index + 1}
                    variant={index + 1 === page ? "outlined" : "text"}
                    size="sm"
                    onClick={() => {
                      setPage(index + 1);
                      setIsLoading(null);
                    }}
                    disabled={totalPage > 1 ? false : true}
                  >
                    {index + 1}
                  </IconButton>
                ))
                  .reduce((acc, button, index, array) => {
                    if (
                      index < 3 ||
                      index >= totalPage - 3 ||
                      (index >= Math.max(page - 1, 3) &&
                        index <= Math.min(page + 1, totalPage - 4))
                    ) {
                      acc.push(button);
                    } else if (acc[acc.length - 1] !== "ellipsis") {
                      acc.push("ellipsis");
                    }
                    return acc;
                  }, [])
                  .map((element, index, array) => {
                    return element === "ellipsis" ? (
                      <IconButton
                        key={`ellipsis-${index}`}
                        variant="text"
                        size="sm"
                        disabled
                      >
                        ...
                      </IconButton>
                    ) : index > 0 && array[index - 1] === "ellipsis" ? (
                      <React.Fragment key={`fragment-${index}`}>
                        {element}
                      </React.Fragment>
                    ) : (
                      element
                    );
                  })}
              </div>

              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setIsLoading(null);
                  setPage(page + 1);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Next
              </Button>
            </CardFooter>
          </>
        )}
      </Card>

      {/* Edit Policy Data */}
      <Dialog
        size="lg"
        open={open}
        handler={handleOpen}
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <form onSubmit={handleEdit}>
          <Card className="mx-auto w-full">
            <CardBody className="overflow-y-auto flex flex-col gap-4 max-h-[calc(100vh-150px)]">
              <Typography variant="h4" color="blue-gray">
                Edit | Policy Management
              </Typography>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Typography className="mb-1 " variant="h6">
                    Title
                  </Typography>
                  <Input
                    type="text"
                    label="Title"
                    size="lg"
                    name="docTitle"
                    value={editFormData?.docTitle}
                    onChange={handleEditChange}
                    onBlur={handleEditBlur}
                    className="focus:!border-2 focus:!border-t-0 focus:!border-black"
                  />
                </div>
                <div className="mb-1">
                  <Typography className="mb-1" variant="h6">
                    Type
                  </Typography>
                  <Select
                    value={editFormData?.docType || ""}
                    label="Type"
                    name="docType"
                    color="purple"
                    disabled
                  >
                    <Option value="">Select Type</Option>
                    <Option value="policy">Policy</Option>
                    <Option value="report">Report</Option>
                    <Option value="sop">SOP</Option>
                    <Option value="diagram">Diagram</Option>
                    <Option value="other">Other</Option>
                  </Select>
                </div>
                <div>
                  <Typography className="mb-1 " variant="h6">
                    Version
                  </Typography>
                  <Input
                    type="text"
                    label="Version"
                    size="lg"
                    name="docVersion"
                    value={editFormData?.docVersion}
                    onChange={handleEditChange}
                    onBlur={handleEditBlur}
                    className="focus:!border-2 focus:!border-t-0 focus:!border-black"
                  />
                </div>
                <div className="mb-1">
                  <Typography className="mb-1 " variant="h6">
                    Owner
                  </Typography>
                  <Select
                    label="Owner"
                    color="purple"
                    name="owner"
                    value={editFormData?.owner || ""}
                    onChange={(e) => handleChangeEditSelect(e, "owner")}
                    onBlur={handleEditBlur}
                  >
                    {Array.isArray(userData) && userData.length > 0 ? (
                      userData.map((resp, index) => (
                        <Option key={resp?.id} value={resp?.id}>
                          {resp?.fullName}
                        </Option>
                      ))
                    ) : (
                      <Option value="">No users available</Option>
                    )}
                  </Select>
                </div>
                <div className="mb-1">
                  <Typography className="mb-1" variant="h6">
                    Revision Frequency
                  </Typography>
                  <Select
                    label="Revision Frequency"
                    name="frequency"
                    color="purple"
                    value={editFormData?.frequency || ""}
                    onChange={(e) => handleChangeEditSelect(e, "frequency")}
                    onBlur={handleEditBlur}
                  >
                    <Option value="">Select Frequency</Option>
                    <Option value="monthly">Monthly</Option>
                    <Option value="quarterly">Quarterly</Option>
                    <Option value="half-Yearly">Half-Yearly</Option>
                    <Option value="yearly">Yearly</Option>
                  </Select>
                </div>
                <div>
                  <Typography className="mb-1 " variant="h6">
                    Status
                  </Typography>
                  <Select
                    label="Status"
                    color="purple"
                    value={editFormData?.status || ""}
                    onChange={(e) => handleChangeEditSelect(e, "status")}
                    onBlur={handleEditBlur}
                  >
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>
                </div>
              </div>
              <div>
                <label
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  for="file_input"
                >
                  Upload file
                </label>
                <input
                  className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
                  id="file_input"
                  onChange={handleEditFileUpload}
                  accept=".pdf"
                  name="file"
                  type="file"
                />
                {errors.file && (
                  <p className="text-red-900 text-[12px]">{errors.file}</p>
                )}
              </div>
              <div>
                <Typography className="mb-1 " variant="h6">
                  Description
                </Typography>
                <Textarea
                  label="Description"
                  size="md"
                  name="description"
                  value={editFormData?.description || ""}
                  onChange={handleEditChange}
                  onBlur={handleEditBlur}
                  className="focus:!border-2 focus:!border-t-0 focus:!border-black"
                />
              </div>
            </CardBody>
            <CardFooter className="flex justify-end border-t-2 py-2">
              <Button
                variant="text"
                color="red"
                onClick={handleOpen}
                className="mr-1"
              >
                <span>Cancel</span>
              </Button>
              {isLoadingButton ? (
                <>
                  <LoadingButton isSetText={true} isLoading={isLoading} />
                </>
              ) : (
                <Button variant="gradient" color="green" type="submit">
                  <span>Update</span>
                </Button>
              )}
            </CardFooter>
          </Card>
        </form>
      </Dialog>

      {/* Create new Policy Data */}
      <Dialog
        size="lg"
        open={openCreateDialog}
        handler={() => setOpenCreateDialog(false)}
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <form onSubmit={handleSubmit}>
          <Card className="mx-auto w-full">
            <CardBody className="overflow-y-auto flex flex-col gap-4 max-h-[calc(100vh-150px)]">
              <Typography variant="h4" color="blue-gray">
                Add | Policy Management
              </Typography>
              <div className="grid grid-cols-2 gap-4">
                <div className="mb-1">
                  <Typography className="mb-1 " variant="h6">
                    Title
                  </Typography>
                  <Input
                    label="Title"
                    size="lg"
                    name="docTitle"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    // value={selectedItem?.integrationProvider}
                    className="focus:!border-2 focus:!border-t-0 focus:!border-black"
                  />
                  {errors.docTitle && (
                    <p className="text-red-900 text-[12px]">
                      {errors.docTitle}
                    </p>
                  )}
                </div>
                <div className="mb-1">
                  <Typography className="mb-1" variant="h6">
                    Type
                  </Typography>
                  <Select
                    onChange={(e) => handleChangeSelect(e, "docType")}
                    onBlur={handleBlur}
                    label="Type"
                    name="docType"
                    color="purple"
                  >
                    <Option value="">Select Type</Option>
                    <Option value="policy">Policy</Option>
                    <Option value="report">Report</Option>
                    <Option value="sop">SOP</Option>
                    <Option value="diagram">Diagram</Option>
                    <Option value="other">Other</Option>
                  </Select>
                  {errors.docType && (
                    <p className="text-red-900 text-[12px]">{errors.docType}</p>
                  )}
                </div>
                <div className="mb-1">
                  <Typography className="mb-1 " variant="h6">
                    Version
                  </Typography>
                  <Input
                    label="Version"
                    size="lg"
                    name="docVersion"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="focus:!border-2 focus:!border-t-0 focus:!border-black"
                  />
                  {errors.docVersion && (
                    <p className="text-red-900 text-[12px]">
                      {errors.docVersion}
                    </p>
                  )}
                </div>
                <div className="mb-1">
                  <Typography className="mb-1 " variant="h6">
                    Owner
                  </Typography>
                  <Select
                    label="Owner"
                    color="purple"
                    name="owner"
                    onBlur={handleBlur}
                    onChange={(e) => handleChangeSelect(e, "owner")}
                    value={formData?.owner}
                  >
                    {Array.isArray(userData) && userData.length > 0 ? (
                      userData.map((resp, index) => (
                        <Option key={index} value={resp?.id}>
                          {resp?.fullName}
                        </Option>
                      ))
                    ) : (
                      <Option value="" disabled>
                        No users available
                      </Option>
                    )}
                  </Select>
                  {errors.owner && (
                    <p className="text-red-900 text-[12px]">{errors.owner}</p>
                  )}
                </div>
                <div className="mb-1">
                  <Typography className="mb-1" variant="h6">
                    Revision Frequency
                  </Typography>
                  <Select
                    onChange={(e) => handleChangeSelect(e, "frequency")}
                    onBlur={handleBlur}
                    label="Revision Frequency"
                    name="frequency"
                    color="purple"
                  >
                    <Option value="">Select Frequency</Option>
                    <Option value="monthly">Monthly</Option>
                    <Option value="quarterly">Quarterly</Option>
                    <Option value="half-yearly">Half-Yearly</Option>
                    <Option value="yearly">Yearly</Option>
                  </Select>
                  {errors.frequency && (
                    <p className="text-red-900 text-[12px]">
                      {errors.frequency}
                    </p>
                  )}
                </div>
                <div>
                  <Typography className="mb-1 " variant="h6">
                    Status
                  </Typography>
                  <Select
                    label="Status"
                    color="purple"
                    onChange={(e) => handleChangeSelect(e, "status")}
                    onBlur={handleBlur}
                  >
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>
                  {errors.status && (
                    <p className="text-red-900 text-[12px]">{errors.status}</p>
                  )}
                </div>
              </div>
              <div>
                <label
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                  for="file_input"
                >
                  Upload file
                </label>
                <input
                  className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
                  id="file_input"
                  onChange={handleFileUpload}
                  accept=".pdf"
                  name="file"
                  type="file"
                />
                {errors.file && (
                  <p className="text-red-900 text-[12px]">{errors.file}</p>
                )}
              </div>
              <div>
                <Typography className="mb-1 " variant="h6">
                  Description
                </Typography>
                <Textarea
                  label="Description"
                  size="md"
                  name="description"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="focus:!border-2 focus:!border-t-0 focus:!border-black"
                />
                {errors.description && (
                  <p className="text-red-900 text-[12px]">
                    {errors.description}
                  </p>
                )}
              </div>
            </CardBody>
            <CardFooter className="flex justify-end border-t-2 py-2">
              <Button
                variant="text"
                color="red"
                onClick={handleOpenCreateDialog}
                className="mr-1"
              >
                <span>Cancel</span>
              </Button>
              {isLoadingButton ? (
                <>
                  <LoadingButton isSetText={true} isLoading={isLoading} />
                </>
              ) : (
                <Button variant="gradient" type="submit" color="green">
                  <span>Create</span>
                </Button>
              )}
            </CardFooter>
          </Card>
        </form>
      </Dialog>

      {/* Delete Policy Data */}
      <Dialog
        size="sm"
        open={deleteDialog}
        handler={setDeleteDialog}
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <Card className="mx-auto w-full p-2">
          <CardBody className="flex flex-col gap-4">
            <Typography variant="h6 mb-1" color="blue-gray">
              Are you sure you want to delete?
            </Typography>
          </CardBody>
          <CardFooter className="flex justify-end py-2">
            <Button
              variant="text"
              color="red"
              onClick={() => {
                setDeleteDialog(false);
                setId("");
              }}
              className="mr-1"
            >
              <span>Cancel</span>
            </Button>

            <Button
              variant="gradient"
              type="submit"
              color="green"
              loading={isLoadingButton}
              isSetText={isLoadingButton ? true : false}
              onClick={() => handleDelete(id)}
            >
              <span>Delete</span>
            </Button>
          </CardFooter>
        </Card>
      </Dialog>
    </div>
  );
}
